import { Configuration, type BaseAPI, UsersApi } from "@/openapi/generated";

import { createOpenApiConfiguration } from "@/lib/utils/api/openapi-configuration";

/**
 * Creates an authenticated API instance with Bearer token for any API class.
 * This is a generic utility that works with all API classes (UsersApi, StudentsApi,
 * PrivatesAppointmentsApi, UserProfileApi, etc.)
 *
 * @example
 * // In a client-side hook:
 * import { useAccessToken } from '@/hooks';
 * import { createAuthenticatedApi } from '@/lib/utils';
 * import { StudentsApi } from '@/openapi/generated';
 *
 * const accessToken = useAccessToken();
 * const service = createAuthenticatedApi(StudentsApi, accessToken);
 *
 * @example
 * // In a server-side prefetch function:
 * import { createAuthenticatedApi } from '@/lib/utils';
 * import { StudentsApi } from '@/openapi/generated';
 *
 * const service = createAuthenticatedApi(StudentsApi, accessToken);
 *
 * @param ApiClass - The API class constructor (e.g., UsersApi, StudentsApi, PrivatesAppointmentsApi, etc.)
 * @param accessToken - The Bearer token to use for authentication
 * @returns Configured API instance with Authorization header set
 */
export function createAuthenticatedApi<T extends BaseAPI>(
  ApiClass: new (configuration?: Configuration) => T,
  accessToken: string,
): T {
  const configuration = createOpenApiConfiguration({
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return new ApiClass(configuration);
}

/**
 * Creates an authenticated UsersApi instance with Bearer token
 * @param accessToken - The Bearer token to use for authentication
 * @returns Configured UsersApi instance
 * @deprecated Use createAuthenticatedApi(UsersApi, accessToken) instead
 */
export function createAuthenticatedUsersApi(accessToken: string) {
  return createAuthenticatedApi(UsersApi, accessToken);
}
